import { useState, useEffect, useRef } from "react";
import { Link } from "@/components/common/Link";
import { Shield, Zap, Droplets, Sun, ArrowRight } from "lucide-react";
export default function ProductFeatures() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, {
      threshold: 0.15
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  const features = [{
    icon: <Shield className="w-6 h-6" />,
    label: "Great anti-dust",
    description: "Advanced surface tension prevents dust accumulation and maintains clarity.",
    num: "01"
  }, {
    icon: <Zap className="w-6 h-6" />,
    label: "Excellent anti scratch",
    description: "Self-healing hydrogen-bond technology repairs minor scratches automatically.",
    num: "02"
  }, {
    icon: <Sun className="w-6 h-6" />,
    label: "Great anti-yellowing",
    description: "Superior UV resistance ensures long-term optical transparency.",
    num: "03"
  }, {
    icon: <Droplets className="w-6 h-6" />,
    label: "Hydrophobic Ability",
    description: "Extreme water-repellent properties for easy cleaning and maintenance.",
    num: "04"
  }];
  const products = [{
    name: "Matrix Dark Black",
    category: "Window Film"
  }, {
    name: "Matrix Light Black",
    category: "Window Film"
  }, {
    name: "Matrix Winshield Film",
    category: "Protection"
  }, {
    name: "Matrix Sunroof Film",
    category: "Specialty"
  }];
  return <section ref={sectionRef} className="dark relative w-full overflow-hidden bg-background text-foreground">
      {}
      <div className="relative w-full h-[60vh] min-h-[400px] flex items-center">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696675/img/features_background_main.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696675/img/features_background_main.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696675/img/features_background_main.png-wvc-sizes" className="absolute inset-0 w-full h-full object-cover object-center" alt="Advanced Coating Technology" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-linear-to-b from-background/20 via-background/60 to-background" />
        <div className="absolute inset-0 bg-linear-to-r from-background/80 via-transparent to-transparent" />
        
        <div className="container relative z-10 mx-auto px-6 md:px-12">
          <div className={`max-w-3xl transition-all duration-1000 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 border border-primary/20 mb-6">
              <span className="w-2 h-2 rounded-lg bg-primary animate-pulse" />
              <span className="text-xs font-bold tracking-widest uppercase text-primary">
                Technical Excellence
              </span>
            </div>
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter mb-6 leading-[0.9]">
              ENGINEERED <br />
              <span className="text-transparent bg-clip-text bg-linear-to-r from-primary to-secondary">
                PERFECTION
              </span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
              Discover the next generation of automotive protection featuring our patented 
              hydrogen-bond coating technology.
            </p>
          </div>
        </div>
      </div>

      {}
      <div className="relative z-10 -mt-20 pb-20">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-lg overflow-hidden shadow-2xl">
            {features.map((feature, i) => <div key={i} data-index={i} className={`group relative bg-card p-8 lg:p-10 transition-all duration-700 hover:bg-accent/5 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <div className="flex flex-col h-full">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform duration-500">
                      {feature.icon}
                    </div>
                    <span className="text-4xl font-black text-muted-foreground/10 group-hover:text-primary/20 transition-colors duration-500">
                      {feature.num}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                    {feature.label}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                    {feature.description}
                  </p>
                  
                  <div className="mt-auto pt-4 border-t border-border/50">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary opacity-0 group-hover:opacity-100 -translate-x-4 group-hover:translate-x-0 transition-all duration-300">
                      Learn More <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {}
      <div className="container mx-auto px-6 md:px-12 pb-24">
        <div className={`flex flex-col lg:flex-row lg:items-center justify-between gap-8 pt-12 border-t border-border transition-all duration-1000 delay-700 ${visible ? "opacity-100" : "opacity-0"}`}>
          <div className="space-y-1">
            <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Product Lineup
            </h4>
            <p className="text-xs text-muted-foreground/60">Explore our specialized film series</p>
          </div>

          <div className="flex flex-wrap gap-4 md:gap-8">
            {products.map((product, i) => <Link key={i} data-index={i} to="#" className="group flex flex-col">
                <span className="text-xs text-primary font-medium mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {product.category}
                </span>
                <span className="text-sm md:text-base font-semibold text-foreground/80 group-hover:text-primary transition-colors relative">
                  {product.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>)}
          </div>
        </div>
      </div>
    </section>;
}